import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import {
  DEFAULT_PAGE_SIZE,
  FACTORY_NATURE_OPTIONS,
  OPERATIONAL_STATUS_OPTIONS,
  getDirectoryCounts,
  getDistrictsWithCounts,
  getFactoriesList,
  getFactoryDetail,
  getManagersForFactory,
  getStatesWithCounts,
} from '../../data/internalDirectoryData';
import { PERMISSION_KEYS, hasPermission } from '../../data/userPermissions';
import StateList from './components/StateList';
import DistrictList from './components/DistrictList';
import FactoryList from './components/FactoryList';
import FactoryDetail from './components/FactoryDetail';

const normalizeRole = (role) => {
  const r = String(role || '').toLowerCase().trim();
  if (r === 'quota manager' || r === 'quotamanager') return 'quota_manager';
  return r;
};

const formatError = (err) => {
  if (!err) return 'Something went wrong.';
  const status = err.status || err.statusCode || err.code;
  const msg = err.message || String(err);
  if (status === 401 || /jwt|not authenticated|invalid claim/i.test(msg)) {
    return 'Authentication required. Please sign in and try again.';
  }
  if (status === 403 || /permission|rls|row-level|policy/i.test(msg)) {
    return 'Access denied. You do not have permission to view the Factory Directory.';
  }
  if (status === 404 || err.code === 'PGRST116') {
    return 'Factory not found.';
  }
  return msg || 'Network error. Please try again.';
};

const FactoryDirectory = () => {
  const navigate = useNavigate();
  const [accessChecked, setAccessChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);

  // Navigation hierarchy: states | districts | factories | detail
  const [view, setView] = useState('states');
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedFactoryId, setSelectedFactoryId] = useState(null);

  // Data
  const [counts, setCounts] = useState({ factories: null, managers: null });
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [factories, setFactories] = useState([]);
  const [factoryTotal, setFactoryTotal] = useState(0);
  const [factory, setFactory] = useState(null);
  const [managers, setManagers] = useState([]);

  // Loading / error
  const [statesLoading, setStatesLoading] = useState(false);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [factoriesLoading, setFactoriesLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [managersLoading, setManagersLoading] = useState(false);
  const [statesError, setStatesError] = useState(null);
  const [districtsError, setDistrictsError] = useState(null);
  const [factoriesError, setFactoriesError] = useState(null);
  const [detailError, setDetailError] = useState(null);

  // Search / filters / pagination
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [operationalStatus, setOperationalStatus] = useState('');
  const [factoryNature, setFactoryNature] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;
  const searchTimer = useRef(null);

  // Access: admin role OR secondary permission internal_factory_directory;
  // always requires a valid Supabase Auth session (table RLS).
  useEffect(() => {
    let cancelled = false;
    const checkAccess = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        if (!cancelled) {
          setAllowed(false);
          setAccessChecked(true);
          navigate('/login-authentication', { replace: true });
        }
        return;
      }

      const role = normalizeRole(localStorage.getItem('userRole'));
      let permitted = role === 'admin';

      if (!permitted) {
        // Prefer profile role from DB when available (more reliable than localStorage alone)
        try {
          const { data: profile } = await supabase
            ?.from('user_profiles')
            ?.select('role')
            ?.eq('user_id', session.user.id)
            ?.maybeSingle();
          if (normalizeRole(profile?.role) === 'admin') {
            permitted = true;
          }
        } catch {
          // ignore — fall through to permission check
        }
      }

      if (!permitted) {
        permitted = await hasPermission(
          session.user.id,
          PERMISSION_KEYS.INTERNAL_FACTORY_DIRECTORY
        );
      }

      if (!cancelled) {
        if (!permitted) {
          setAllowed(false);
          setAccessChecked(true);
          navigate('/login-authentication', { replace: true });
          return;
        }
        setAllowed(true);
        setAccessChecked(true);
      }
    };
    checkAccess();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Debounce search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const loadCounts = useCallback(async () => {
    const res = await getDirectoryCounts();
    if (!res.error) {
      setCounts({ factories: res.factories, managers: res.managers });
    }
  }, []);

  const loadStates = useCallback(async () => {
    setStatesLoading(true);
    setStatesError(null);
    const res = await getStatesWithCounts();
    if (res.error) {
      setStatesError(formatError(res.error));
      setStates([]);
    } else {
      setStates(res.data || []);
    }
    setStatesLoading(false);
  }, []);

  const loadDistricts = useCallback(async (state) => {
    if (!state) return;
    setDistrictsLoading(true);
    setDistrictsError(null);
    const res = await getDistrictsWithCounts(state);
    if (res.error) {
      setDistrictsError(formatError(res.error));
      setDistricts([]);
    } else {
      setDistricts(res.data || []);
    }
    setDistrictsLoading(false);
  }, []);

  const loadFactories = useCallback(async () => {
    // Search mode: allow without district; hierarchy mode: need state+district unless search/filters active globally
    const hasSearchOrFilter = Boolean(search || operationalStatus || factoryNature);
    if (!hasSearchOrFilter && (!selectedState || !selectedDistrict)) {
      return;
    }

    setFactoriesLoading(true);
    setFactoriesError(null);
    const res = await getFactoriesList({
      state: selectedState || null,
      district: selectedDistrict || null,
      search: search || '',
      operationalStatus: operationalStatus || null,
      factoryNature: factoryNature || null,
      page,
      pageSize,
    });

    if (res.error) {
      setFactoriesError(formatError(res.error));
      setFactories([]);
      setFactoryTotal(0);
    } else {
      setFactories(res.data || []);
      setFactoryTotal(res.total || 0);
    }
    setFactoriesLoading(false);
  }, [selectedState, selectedDistrict, search, operationalStatus, factoryNature, page, pageSize]);

  const loadDetail = useCallback(async (factoryId) => {
    if (!factoryId) return;
    setDetailLoading(true);
    setManagersLoading(true);
    setDetailError(null);
    setFactory(null);
    setManagers([]);

    const [facRes, mgrRes] = await Promise.all([
      getFactoryDetail(factoryId),
      getManagersForFactory(factoryId),
    ]);

    if (facRes.error) {
      setDetailError(formatError(facRes.error));
      setFactory(null);
    } else {
      setFactory(facRes.data);
    }
    setDetailLoading(false);

    if (mgrRes.error) {
      // managers failure should not block detail
      setManagers([]);
    } else {
      setManagers(mgrRes.data || []);
    }
    setManagersLoading(false);
  }, []);

  // Initial load
  useEffect(() => {
    if (!allowed) return;
    loadCounts();
    loadStates();
  }, [allowed, loadCounts, loadStates]);

  // When entering factories view or filters change
  useEffect(() => {
    if (!allowed) return;
    if (view === 'factories') {
      loadFactories();
    }
  }, [allowed, view, loadFactories]);

  // Detail load
  useEffect(() => {
    if (!allowed) return;
    if (view === 'detail' && selectedFactoryId) {
      loadDetail(selectedFactoryId);
    }
  }, [allowed, view, selectedFactoryId, loadDetail]);

  const hasActiveFilters = Boolean(search || operationalStatus || factoryNature);

  const handleSelectState = (state) => {
    setSelectedState(state);
    setSelectedDistrict(null);
    setSelectedFactoryId(null);
    setPage(1);
    setView('districts');
    loadDistricts(state);
  };

  const handleSelectDistrict = (district) => {
    setSelectedDistrict(district);
    setSelectedFactoryId(null);
    setPage(1);
    setView('factories');
  };

  const handleSelectFactory = (f) => {
    setSelectedFactoryId(f.id);
    setView('detail');
  };

  const handleBack = () => {
    if (view === 'detail') {
      setSelectedFactoryId(null);
      setFactory(null);
      setManagers([]);
      setView('factories');
      return;
    }
    if (view === 'factories') {
      if (hasActiveFilters && !selectedDistrict) {
        // global search mode → back to states
        setView('states');
        return;
      }
      setSelectedDistrict(null);
      setView('districts');
      return;
    }
    if (view === 'districts') {
      setSelectedState(null);
      setDistricts([]);
      setView('states');
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearch('');
    setOperationalStatus('');
    setFactoryNature('');
    setPage(1);
    // If user was browsing via search/filters without a district, return to hierarchy
    if (!selectedDistrict) {
      if (selectedState) {
        setView('districts');
        loadDistricts(selectedState);
      } else {
        setView('states');
      }
    }
  };

  const handleSearchFocusMode = () => {
    // When user searches from any level, show factories results
    if (searchInput.trim() || operationalStatus || factoryNature) {
      setView('factories');
    }
  };

  // When search/filters become active, switch to factories list view
  useEffect(() => {
    if (!allowed) return;
    if (search || operationalStatus || factoryNature) {
      if (view === 'states' || view === 'districts') {
        setView('factories');
      }
    }
  }, [search, operationalStatus, factoryNature, allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusOptions = useMemo(
    () => [
      { value: '', label: 'All statuses' },
      ...OPERATIONAL_STATUS_OPTIONS.map((v) => ({ value: v, label: v })),
    ],
    []
  );

  const natureOptions = useMemo(
    () => [
      { value: '', label: 'All natures' },
      ...FACTORY_NATURE_OPTIONS.map((v) => ({ value: v, label: v })),
    ],
    []
  );

  const breadcrumbParts = useMemo(() => {
    const parts = [{ label: 'All States', onClick: () => {
      setView('states');
      setSelectedState(null);
      setSelectedDistrict(null);
      setSelectedFactoryId(null);
    } }];
    if (selectedState) {
      parts.push({
        label: selectedState,
        onClick: () => {
          setView('districts');
          setSelectedDistrict(null);
          setSelectedFactoryId(null);
          loadDistricts(selectedState);
        },
      });
    }
    if (selectedDistrict) {
      parts.push({
        label: selectedDistrict,
        onClick: () => {
          setView('factories');
          setSelectedFactoryId(null);
        },
      });
    }
    if (view === 'detail' && factory?.factoryName) {
      parts.push({ label: factory.factoryName, onClick: null });
    }
    return parts;
  }, [selectedState, selectedDistrict, view, factory, loadDistricts]);

  if (!accessChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking access…</p>
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Factory Directory | SugarHathi</title>
      </Helmet>
      <RoleBasedHeader />
      <BreadcrumbNavigation />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">
              Factory Directory
            </h1>
            <p className="text-xs sm:text-sm mt-1 text-muted-foreground">
              Internal directory of sugar factories and contacts
              {counts.factories != null && (
                <span>
                  {' '}
                  · {counts.factories} factories
                  {counts.managers != null ? ` · ${counts.managers} managers` : ''}
                </span>
              )}
            </p>
          </div>
          {view !== 'states' && (
            <Button variant="outline" size="sm" onClick={handleBack} iconName="ArrowLeft" iconPosition="left">
              Back
            </Button>
          )}
        </div>

        {/* Directory hierarchy trail */}
        <nav
          className="flex flex-wrap items-center gap-1 text-xs sm:text-sm mb-4"
          aria-label="Directory hierarchy"
        >
          {breadcrumbParts.map((part, idx) => (
            <React.Fragment key={`${part.label}-${idx}`}>
              {idx > 0 && (
                <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
              )}
              {part.onClick && idx < breadcrumbParts.length - 1 ? (
                <button
                  type="button"
                  onClick={part.onClick}
                  className="font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  style={{ color: '#1e4a72' }}
                >
                  {part.label}
                </button>
              ) : (
                <span
                  className={`font-medium ${
                    idx === breadcrumbParts.length - 1 ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {part.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* Search + filters */}
        <div className="rounded-lg border border-border bg-card p-3 sm:p-4 mb-5 space-y-3 shadow-sm">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <Input
                type="search"
                placeholder="Search factory, plant, mill code, manager, designation…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e?.target?.value ?? '');
                  handleSearchFocusMode();
                }}
                aria-label="Search factories"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:w-[420px] w-full">
              <Select
                options={statusOptions}
                value={operationalStatus}
                onChange={(v) => {
                  setOperationalStatus(v || '');
                  setPage(1);
                }}
                placeholder="Operational status"
              />
              <Select
                options={natureOptions}
                value={factoryNature}
                onChange={(v) => {
                  setFactoryNature(v || '');
                  setPage(1);
                }}
                placeholder="Factory nature"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Filters active
                {selectedState ? ` · scoped to ${selectedState}` : ''}
                {selectedDistrict ? ` / ${selectedDistrict}` : ''}
              </p>
              <button
                type="button"
                onClick={handleClearFilters}
                className="text-xs font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                style={{ color: '#1e4a72' }}
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Views */}
        {view === 'states' && (
          <StateList
            states={states}
            loading={statesLoading}
            error={statesError}
            onSelect={handleSelectState}
            onRetry={loadStates}
          />
        )}

        {view === 'districts' && (
          <DistrictList
            districts={districts}
            stateName={selectedState}
            loading={districtsLoading}
            error={districtsError}
            onSelect={handleSelectDistrict}
            onRetry={() => loadDistricts(selectedState)}
          />
        )}

        {view === 'factories' && (
          <FactoryList
            factories={factories}
            total={factoryTotal}
            page={page}
            pageSize={pageSize}
            loading={factoriesLoading}
            error={factoriesError}
            onSelect={handleSelectFactory}
            onRetry={loadFactories}
            onPageChange={(p) => setPage(p)}
          />
        )}

        {view === 'detail' && (
          <FactoryDetail
            factory={factory}
            managers={managers}
            loading={detailLoading}
            managersLoading={managersLoading}
            error={detailError}
            onRetry={() => loadDetail(selectedFactoryId)}
            onBack={handleBack}
          />
        )}
      </main>
    </div>
  );
};

export default FactoryDirectory;
